import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { currency } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const emptyAddress = { label: "Home", full_name: "", phone: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "India", is_default: false };

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const [address, setAddress] = useState(emptyAddress);
  const [coupon, setCoupon] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [payment, setPayment] = useState("stripe");
  const [totals, setTotals] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (items.length === 0) return;
    (async () => {
      const { data } = await api.post("/checkout/totals", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        address, payment_method: payment, coupon_code: couponData?.code, origin_url: window.location.origin,
      });
      setTotals(data);
    })().catch(() => {});
  }, [items, couponData, payment]);

  useEffect(() => {
    if (user) {
      const def = user.addresses?.find((a) => a.is_default) || user.addresses?.[0];
      if (def) setAddress({ ...def });
      else setAddress((a) => ({ ...a, full_name: user.name || "" }));
    }
  }, [user]);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post(`/coupons/validate?code=${encodeURIComponent(coupon)}&subtotal=${subtotal}`);
      setCouponData(data);
      toast.success(`Coupon applied — ${data.type === "percent" ? data.amount + "% off" : currency(data.discount) + " off"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid coupon"); }
  };

  const placeOrder = async () => {
    if (!address.full_name || !address.phone || !address.line1 || !address.city || !address.postal_code) { toast.error("Please complete the address"); return; }
    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        address,
        payment_method: payment,
        coupon_code: couponData?.code || null,
        origin_url: window.location.origin,
        note,
      };
      const { data } = await api.post("/checkout", payload);
      if (data.payment_method === "cod") {
        clear();
        nav(`/payment/success?order_id=${data.order_id}`);
      } else if (data.checkout_url) {
        clear();
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to place order");
    } finally { setPlacing(false); }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 py-32 text-center">
        <div className="font-serif text-3xl text-charcoal/60">Your bag is empty.</div>
        <Link to="/shop" className="mt-6 inline-block overline text-emerald">Enter the atelier</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="checkout-page">
      <div className="border-b divider-gold pb-8">
        <div className="overline text-emerald mb-3">Almost there</div>
        <h1 className="font-serif text-5xl tracking-tight">Checkout</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-16 mt-12">
        <div className="space-y-12">
          {/* Address */}
          <section>
            <div className="overline text-emerald mb-4">01 · Delivery</div>
            <h2 className="font-serif text-2xl mb-6">Where should we send it?</h2>
            {user && user.addresses?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                {user.addresses.map((a) => (
                  <button key={a.id} onClick={() => setAddress({ ...a })} className={`text-left p-4 border ${address.id === a.id ? "border-emerald" : "divider-gold"} bg-ivory/40 hover:bg-ivory/70`} data-testid={`saved-address-${a.id}`}>
                    <div className="font-serif text-lg">{a.label}</div>
                    <div className="text-xs text-charcoal/60 mt-1">{a.full_name} · {a.phone}</div>
                    <div className="text-xs text-charcoal/60 mt-1">{a.line1}, {a.city} {a.postal_code}</div>
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Input placeholder="Full name" required value={address.full_name} onChange={(e) => setAddress({ ...address, full_name: e.target.value })} data-testid="addr-name" />
              <Input placeholder="Phone" required value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} data-testid="addr-phone" />
              <Input placeholder="Address line 1" required value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} data-testid="addr-line1" className="col-span-2" />
              <Input placeholder="Address line 2 (optional)" value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} className="col-span-2" />
              <Input placeholder="City" required value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} data-testid="addr-city" />
              <Input placeholder="State" required value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} data-testid="addr-state" />
              <Input placeholder="Postal code" required value={address.postal_code} onChange={(e) => setAddress({ ...address, postal_code: e.target.value })} data-testid="addr-zip" />
              <Input placeholder="Country" value={address.country} onChange={(e) => setAddress({ ...address, country: e.target.value })} />
            </div>
            <textarea placeholder="Order notes (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="mt-4 w-full p-3 border divider-gold rounded-none bg-transparent text-sm min-h-[80px]" />
          </section>

          {/* Payment */}
          <section>
            <div className="overline text-emerald mb-4">02 · Payment</div>
            <h2 className="font-serif text-2xl mb-6">How would you like to pay?</h2>
            <RadioGroup value={payment} onValueChange={setPayment}>
              <label className="flex items-start gap-4 p-5 border divider-gold cursor-pointer hover:bg-ivory/40" data-testid="pay-stripe">
                <RadioGroupItem value="stripe" id="stripe" className="mt-1" />
                <div className="flex-1">
                  <div className="font-serif text-lg">Card · Stripe secured</div>
                  <div className="text-xs text-charcoal/60 mt-1">Pay with any card. Test card: 4242 4242 4242 4242, any future expiry, any CVC.</div>
                </div>
              </label>
              <label className="flex items-start gap-4 p-5 border divider-gold cursor-pointer hover:bg-ivory/40" data-testid="pay-cod">
                <RadioGroupItem value="cod" id="cod" className="mt-1" />
                <div className="flex-1">
                  <div className="font-serif text-lg">Cash on delivery</div>
                  <div className="text-xs text-charcoal/60 mt-1">Pay when your parcel arrives. Available across India.</div>
                </div>
              </label>
            </RadioGroup>
          </section>
        </div>

        {/* Summary */}
        <aside className="bg-ivory/60 p-8 h-fit lg:sticky lg:top-28">
          <div className="overline text-emerald mb-4">Your order</div>
          <div className="space-y-4 max-h-[300px] overflow-auto pr-2 mb-6">
            {items.map((i) => (
              <div key={i.product_id} className="flex gap-3">
                <img src={i.image} className="w-14 h-16 object-cover bg-ivory" alt="" />
                <div className="flex-1 text-sm">
                  <div className="font-serif">{i.name}</div>
                  <div className="text-xs text-charcoal/60">Qty {i.quantity}</div>
                </div>
                <div className="text-sm">{currency(i.price * i.quantity)}</div>
              </div>
            ))}
          </div>

          <div className="flex gap-2 mb-4">
            <Input placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value)} className="rounded-none" data-testid="coupon-input" />
            <Button variant="outline" onClick={applyCoupon} data-testid="coupon-apply" className="rounded-none border-emerald text-emerald hover:bg-emerald hover:text-ivory">Apply</Button>
          </div>

          {totals && (
            <div className="text-sm space-y-2 border-t divider-gold pt-4">
              <div className="flex justify-between"><span>Subtotal</span><span>{currency(totals.subtotal)}</span></div>
              {totals.discount > 0 && <div className="flex justify-between text-emerald"><span>Discount</span><span>−{currency(totals.discount)}</span></div>}
              <div className="flex justify-between text-charcoal/70"><span>Shipping</span><span>{totals.shipping === 0 ? "Complimentary" : currency(totals.shipping)}</span></div>
              <div className="flex justify-between text-charcoal/70"><span>Tax</span><span>{currency(totals.tax)}</span></div>
              <div className="flex justify-between items-baseline pt-3 border-t divider-gold mt-3">
                <span className="overline">Total</span>
                <span className="font-serif text-2xl" data-testid="checkout-total">{currency(totals.total)}</span>
              </div>
            </div>
          )}

          <Button data-testid="place-order" onClick={placeOrder} disabled={placing} className="mt-6 w-full rounded-none bg-emerald hover:bg-emerald/90 text-ivory h-12 tracking-[0.15em] uppercase text-xs">
            {placing ? "Placing…" : payment === "cod" ? "Place order · COD" : "Continue to payment →"}
          </Button>
        </aside>
      </div>
    </div>
  );
}
